// Pure helpers used by the team_assist_proposed_action component. Lives in
// its own module so unit tests can exercise them without pulling in the React
// component tree (and its transitive imports — Button, FontAwesome icons,
// etc., which require a DOM-ish environment).

// extractSchemaProperties returns an ordered list of editable field
// descriptors from the destructive tool's JSON Schema. Unrecognised types
// fall back to a single-line text input. Returns [] when no schema is
// present so callers can fall back to the raw-args preview.
export function extractSchemaProperties(inputSchema) {
  if (!inputSchema || typeof inputSchema !== 'object') {
    return [];
  }
  const properties = inputSchema.properties;
  if (!properties || typeof properties !== 'object') {
    return [];
  }
  const requiredSet = new Set(Array.isArray(inputSchema.required) ? inputSchema.required : []);
  const fields = Object.keys(properties).map(name => {
    const spec = properties[name] || {};
    return {
      name,
      label: spec.title || name,
      description: spec.description || '',
      type: inferFieldType(spec),
      enumValues: Array.isArray(spec.enum) ? spec.enum : null,
      // Labeled options derived from a `oneOf` of {const, title} entries —
      // the backend folds an id-typed field's enumerated candidates into this
      // shape so the agent picks a human-readable label that resolves to the
      // underlying id. null when the field is not a labeled select.
      options: oneOfOptions(spec),
      // Name of the field this one depends on (cascading select), e.g.
      // lineItemId dependsOn orderId. null when independent. Each option then
      // carries a `parentValue` identifying which parent it belongs to.
      dependsOn: typeof spec['x-dependsOn'] === 'string' ? spec['x-dependsOn'] : null,
      required: requiredSet.has(name),
    };
  });
  return orderByDependency(fields);
}

// orderByDependency reorders fields so a dependent (cascading) field always
// follows the parent it depends on — the agent must pick the order before the
// line item. Declaration order is otherwise preserved (stable): each field is
// emitted in turn, but its parent (and any ancestors) are pulled ahead first.
// A dependsOn that references a missing or cyclic field is ignored rather than
// looping.
export function orderByDependency(fields) {
  const byName = new Map(fields.map(field => [field.name, field]));
  const ordered = [];
  const placed = new Set();
  const visiting = new Set();
  const place = field => {
    if (placed.has(field.name) || visiting.has(field.name)) {
      return;
    }
    visiting.add(field.name);
    if (field.dependsOn && byName.has(field.dependsOn)) {
      place(byName.get(field.dependsOn));
    }
    visiting.delete(field.name);
    placed.add(field.name);
    ordered.push(field);
  };
  fields.forEach(place);
  return ordered;
}

// oneOfOptions extracts {value, label, parentValue?} entries from a JSON Schema
// `oneOf` of {const, title, x-parentValue?} entries. `parentValue` is carried
// through for dependent (cascading) selects so the option can be filtered by
// the selected parent. Returns null when the field has no such oneOf so callers
// can distinguish a labeled select from a bare enum / text field.
export function oneOfOptions(spec) {
  if (!Array.isArray(spec.oneOf)) {
    return null;
  }
  const options = spec.oneOf
    .filter(entry => entry && Object.prototype.hasOwnProperty.call(entry, 'const'))
    .map(entry => {
      const option = { value: entry.const, label: entry.title || String(entry.const) };
      if (typeof entry['x-parentValue'] === 'string') {
        option.parentValue = entry['x-parentValue'];
      }
      return option;
    });
  return options.length > 0 ? options : null;
}

// visibleOptionsFor returns the options a (possibly dependent) select should
// show given the current form values. For a field with `dependsOn`, only
// options whose `parentValue` matches the selected parent are shown, and none
// are shown until the parent is selected — so the agent can't pick a child that
// belongs to a different parent. Independent fields return all their options.
export function visibleOptionsFor(field, formValues) {
  if (!field.dependsOn) {
    // Pass labeled options through unchanged, and `null` through as `null` so a
    // bare-enum field (no labeled options) still falls back to its enumValues
    // in the renderer rather than rendering an empty select.
    return field.options;
  }
  const options = field.options || [];
  const parentValue = formValues[field.dependsOn];
  if (parentValue == null || parentValue === '') {
    return [];
  }
  return options.filter(option => option.parentValue === String(parentValue));
}

// applyFieldChange returns the next form values with `name` set to `value`,
// then cascades resets: any field that (transitively) depends on `name` is
// cleared when its current value is no longer among its visible options. This
// keeps a stale child id from surviving a parent change and reaching execution.
export function applyFieldChange(formValues, schemaProperties, name, value) {
  return pruneStaleDependents({ ...formValues, [name]: value }, schemaProperties);
}

// pruneStaleDependents clears any dependent (cascading) field whose current
// value is not among its visible options given the other form values, mutating
// and returning `values`. Re-pruning until stable handles dependency chains
// (A -> B -> C): clearing B can in turn invalidate C. Shared by applyFieldChange
// (edit time) and initialFormValuesFor (seed time) so a child id that doesn't
// belong to its parent is never executable — whether the agent mistyped it or
// the model pre-filled a mismatched pair.
function pruneStaleDependents(values, schemaProperties) {
  let changed = true;
  while (changed) {
    changed = false;
    schemaProperties.forEach(field => {
      if (!field.dependsOn) {
        return;
      }
      const current = values[field.name];
      if (current == null || current === '') {
        return;
      }
      const visible = visibleOptionsFor(field, values);
      if (!visible.some(option => String(option.value) === String(current))) {
        values[field.name] = '';
        changed = true;
      }
    });
  }
  return values;
}

export function inferFieldType(spec) {
  if (oneOfOptions(spec) || (Array.isArray(spec.enum) && spec.enum.length > 0)) {
    return 'enum';
  }
  switch (spec.type) {
    case 'integer':
    case 'number':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'string':
      // The backend only emits the `date-time` JSON Schema format, never
      // `date`, so any other format (including `date`) falls back to text.
      if (spec.format === 'date-time') {
        return 'date';
      }
      return 'text';
    default:
      return 'text';
  }
}

// initialFormValuesFor prefills the form from the LLM's proposed Arguments
// blob (object or JSON-string). Only keys declared in the schema are kept —
// downstream the form value object becomes the executed arguments, and the
// schema is the contract. Date fields are seeded in the same datetime-local
// display form the control renders (see iso8601ToDatetimeLocal), so a value
// the agent can't see — whitespace, a non-string, or an unparseable date —
// collapses to "" in state rather than lingering as a hidden payload that
// findMissingRequired would treat as present and Execute would send unchanged.
// A dependent (cascading) field seeded with a value that doesn't belong to its
// seeded parent — the "most common" model mistake, e.g. a lineItemId from a
// different orderId — is pruned to "" too, so the same provenance check that
// runs on edit also gates the model's initial pre-fill.
export function initialFormValuesFor(args, schemaProperties) {
  const parsed = parseProposedActionArguments(args);
  const initial = {};
  schemaProperties.forEach(field => {
    if (Object.prototype.hasOwnProperty.call(parsed, field.name)) {
      const value = parsed[field.name];
      initial[field.name] = field.type === 'date' ? iso8601ToDatetimeLocal(value) : value;
    } else {
      initial[field.name] = field.type === 'boolean' ? false : '';
    }
  });
  return pruneStaleDependents(initial, schemaProperties);
}

export function parseProposedActionArguments(args) {
  if (args == null) {
    return {};
  }
  if (typeof args === 'string') {
    if (!args.trim()) {
      return {};
    }
    try {
      const parsed = JSON.parse(args);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
      return {};
    }
  }
  if (typeof args === 'object') {
    return args;
  }
  return {};
}

// datetimeLocalToIso8601 converts an <input type="datetime-local"> value
// (local wall-clock with no seconds or zone, e.g. "2026-06-11T15:01") into a
// full ISO 8601 instant in UTC ("2026-06-11T22:01:00Z"). The backend tool
// schema's `date-time` format requires seconds and a timezone, neither of
// which the native control emits; the zone is the agent's browser-local zone,
// matching how the rest of the app derives timezone (Intl / moment.tz.guess).
// Fractional seconds are deliberately dropped — the backend's RFC 3339
// validator expects the second-precision form (e.g. "2026-06-12T14:00:00Z"),
// not the millisecond form Date.toISOString() emits by default.
// Returns the input unchanged when it is empty or not parseable so we never
// silently drop what the agent entered.
export function datetimeLocalToIso8601(value) {
  if (typeof value !== 'string' || !value.trim()) {
    return value;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

// iso8601ToDatetimeLocal converts a full ISO 8601 instant (the wire format the
// LLM prefills date-time arguments in) into the local-time "YYYY-MM-DDThh:mm"
// string the native datetime-local control requires to display a value.
// Returns "" for anything the control can't render — empty, whitespace,
// non-string, or an unparseable date — mirroring what the control actually
// shows for such a value (nothing). This is also the canonical "what the field
// displays" form used to seed form state, so validation and submission operate
// on the same value the agent sees; re-rendering a value already in the bare
// datetime-local shape is a no-op round-trip.
export function iso8601ToDatetimeLocal(value) {
  if (typeof value !== 'string' || !value.trim()) {
    return '';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  const pad = n => String(n).padStart(2, '0');
  return (
    `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}` +
    `T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`
  );
}

// normalizeFormValuesForSubmit converts editable field values into the wire
// representation the backend expects before they become the executed
// arguments. Only date-time fields need adjustment today: their datetime-local
// input value is promoted to a full ISO 8601 instant.
export function normalizeFormValuesForSubmit(formValues, schemaProperties) {
  const normalized = { ...formValues };
  schemaProperties.forEach(field => {
    if (field.type === 'date') {
      normalized[field.name] = datetimeLocalToIso8601(normalized[field.name]);
    }
  });
  return normalized;
}

export function findMissingRequired(formValues, schemaProperties) {
  return schemaProperties
    .filter(field => field.required)
    .filter(field => {
      const value = formValues[field.name];
      if (value == null) return true;
      if (typeof value === 'string') return value.trim() === '';
      return false;
    })
    .map(field => field.name);
}

// formatToolDisplayName renders a destructive external tool name as
// `<action> (<config>)`, where <action> is the substring after the last '-'
// and <config> is the substring before the first '-'. The MCP tool naming
// convention is `<configName>-<source>-<actionName>`, so this surfaces the
// agent-meaningful action name with the configuration that owns it in
// parentheses. Falls back to the raw name when it has no '-' or when either
// side would be empty.
export function formatToolDisplayName(toolName) {
  if (!toolName) {
    return '';
  }
  if (!toolName.includes('-')) {
    return toolName;
  }
  const lastDash = toolName.lastIndexOf('-');
  const firstDash = toolName.indexOf('-');
  const action = toolName.slice(lastDash + 1);
  const config = toolName.slice(0, firstDash);
  if (!action || !config) {
    return toolName;
  }
  return `${action} (${config})`;
}

// formatProposedActionArguments renders the LLM-emitted argument blob as
// pretty-printed JSON text. Always returns text — never HTML — so the
// caller can safely place it inside a <pre> without dangerouslySetInnerHTML.
// Accepts either a plain object or a JSON string (the wire format from
// supernova).
export function formatProposedActionArguments(args) {
  if (args == null) {
    return '';
  }
  if (typeof args === 'string') {
    if (!args.trim()) {
      return '';
    }
    try {
      return JSON.stringify(JSON.parse(args), null, 2);
    } catch (e) {
      return args;
    }
  }
  try {
    return JSON.stringify(args, null, 2);
  } catch (e) {
    return String(args);
  }
}
